import React, { useState } from 'react';
import { 
  FaShieldAlt, 
  FaGavel, 
  FaShoppingCart, 
  FaTruck, 
  FaExchangeAlt, 
  FaCreditCard,
  FaUserShield,
  FaFileContract,
  FaCheckCircle,
  FaPrint,
  FaDownload,
  FaHome,
  FaArrowRight,
  FaGem,
  FaRing,
  FaCrown
} from 'react-icons/fa';

const TermsPage = () => {
  const [lastUpdated] = useState('April 15, 2026');
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    {
      id: 'overview',
      title: 'Overview',
      icon: <FaFileContract className="w-4 h-4" />,
      content: [
        'These Terms and Conditions govern your use of the SKK Jewelry website and your purchase of products from our store.',
        'By accessing our website or making a purchase, you agree to be bound by these terms. Please read them carefully before placing any order.',
        'If you do not agree with any part of these terms, you may not use our website or services.'
      ]
    },
    {
      id: 'account',
      title: 'Account Registration',
      icon: <FaUserShield className="w-4 h-4" />,
      content: [
        'To place an order, you may need to create an account with us.',
        'You are responsible for maintaining the confidentiality of your account credentials.',
        'You agree to provide accurate, current, and complete information during registration.',
        'You are responsible for all activities that occur under your account.',
        'We reserve the right to refuse service, terminate accounts, or cancel orders at our sole discretion.'
      ]
    },
    {
      id: 'orders',
      title: 'Order Placement & Acceptance',
      icon: <FaShoppingCart className="w-4 h-4" />,
      content: [
        'All orders are subject to acceptance and availability.',
        'Once you place an order, you will receive an email confirmation acknowledging your order details.',
        'We reserve the right to cancel any order for any reason, including product availability, pricing errors, or suspected fraud.',
        'If we cancel your order, you will receive a full refund.',
        'We may contact you to verify order details before processing.'
      ]
    },
    {
      id: 'pricing',
      title: 'Pricing & Payment',
      icon: <FaCreditCard className="w-4 h-4" />,
      content: [
        'All prices are listed in USD and are subject to change without notice.',
        'Prices do not include applicable taxes or shipping charges, which will be added at checkout.',
        'We accept various payment methods including credit cards, debit cards, and digital wallets.',
        'All payments are processed through secure payment gateways.',
        'We are not responsible for any additional fees charged by your bank or payment provider.'
      ]
    },
    {
      id: 'shipping',
      title: 'Shipping & Delivery',
      icon: <FaTruck className="w-4 h-4" />,
      content: [
        'We ship to addresses within India and select international locations.',
        'Estimated delivery times are provided at checkout but are not guaranteed.',
        'We are not responsible for delays caused by shipping carriers, customs, or natural events.',
        'A tracking number will be provided once your order ships.',
        'Risk of loss passes to you upon delivery of the product to the shipping carrier.'
      ]
    },
    {
      id: 'returns',
      title: 'Returns & Refunds',
      icon: <FaExchangeAlt className="w-4 h-4" />,
      content: [
        'We offer a 30-day return policy on most products.',
        'Items must be unused, unworn, and in original packaging with all tags attached.',
        'Customized or personalized items cannot be returned unless defective.',
        'Refunds will be processed within 7-10 business days of receiving the returned item.',
        'Original shipping charges are non-refundable unless the return is due to our error.'
      ]
    },
    {
      id: 'warranty',
      title: 'Product Warranty',
      icon: <FaShieldAlt className="w-4 h-4" />,
      content: [
        'Our jewelry products come with a 1-year limited warranty against manufacturing defects.',
        'Warranty does not cover normal wear and tear, damage from accidents, or improper care.',
        'To claim warranty, please contact our customer support with proof of purchase.',
        'We reserve the right to repair, replace, or refund the product at our discretion.',
        'Gemstone treatments and enhancements are disclosed in product descriptions.'
      ]
    },
    {
      id: 'intellectual',
      title: 'Intellectual Property',
      icon: <FaGavel className="w-4 h-4" />,
      content: [
        'All content on our website, including images, text, logos, and designs, is our property or licensed to us.',
        'You may not reproduce, distribute, or create derivative works without our written permission.',
        'SKK Jewelry and our product names are trademarks owned by us.',
        'Unauthorized use of our intellectual property may result in legal action.'
      ]
    }
  ];

  const importantPolicies = [
    {
      title: 'Product Authenticity',
      description: 'All our jewelry products come with authenticity certificates and are sourced from certified suppliers.',
      icon: <FaGem className="w-5 h-5" />
    },
    {
      title: 'Secure Transactions',
      description: 'Your payment information is encrypted and processed through PCI-compliant secure gateways.',
      icon: <FaCreditCard className="w-5 h-5" />
    },
    {
      title: 'Privacy Protection',
      description: 'We never share your personal information with third parties without your consent.',
      icon: <FaUserShield className="w-5 h-5" />
    },
    {
      title: 'Quality Guarantee',
      description: 'We stand behind the quality of our products and offer hassle-free returns.',
      icon: <FaCheckCircle className="w-5 h-5" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-700 via-pink-700 to-amber-700 text-white">
        <div className="container mx-auto px-4 py-8 md:py-12">
          {/* Navigation Links */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <FaHome className="w-4 h-4" />
                <span className="text-sm">Home</span>
              </a>
              <span className="text-white/40">/</span>
              <span className="text-sm font-medium">Terms & Conditions</span>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => window.print()}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-all text-sm"
              >
                <FaPrint className="w-3.5 h-3.5" />
                Print
              </button>
              <button className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-all text-sm">
                <FaDownload className="w-3.5 h-3.5" />
                Download PDF
              </button>
            </div>
          </div>

          {/* Main Header */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
              <FaGavel className="w-4 h-4" />
              <span className="text-sm font-semibold">Terms & Conditions</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
              Our Terms & Conditions
            </h1>
            <p className="text-purple-100 max-w-2xl mx-auto text-sm md:text-base">
              Please read these terms carefully before using our website or making a purchase.
            </p>
            <div className="mt-4 text-sm text-purple-200">
              Last Updated: {lastUpdated}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12 max-w-6xl">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 bg-white rounded-xl shadow-md p-4 border border-purple-100">
              <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                <FaFileContract className="w-4 h-4 text-purple-600" />
                Quick Navigation
              </h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200"
                  >
                    <span className="text-purple-500">{section.icon}</span>
                    <span>{section.title}</span>
                  </a>
                ))}
                <a
                  href="#contact"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200"
                >
                  <FaShoppingCart className="w-4 h-4 text-purple-500" />
                  <span>Contact Support</span>
                </a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-100">
              {/* Introduction */}
              <div className="p-6 md:p-8 border-b border-gray-200 bg-gradient-to-r from-purple-50/30 to-pink-50/30">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                    <FaGavel className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Terms and Conditions
                  </h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Welcome to SKK Jewelry. These Terms and Conditions constitute a legally binding agreement 
                  between you and SKK Jewelry regarding your use of our website and purchase of our products.
                </p>
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-amber-800 text-sm flex items-start gap-2">
                    <span className="text-amber-600 text-lg">⚖️</span>
                    <span>By using our website, you acknowledge that you have read, understood, and agree to be bound by these terms.</span>
                  </p>
                </div>
              </div>

              {/* Sections */}
              {sections.map((section) => (
                <div
                  key={section.id}
                  id={section.id}
                  className="p-6 md:p-8 border-b border-gray-200 scroll-mt-20"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center text-purple-600">
                      {section.icon}
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-800">
                      {section.title}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {section.content.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <FaCheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Important Policies Section */}
              <div className="p-6 md:p-8 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                    <FaShieldAlt className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">
                    Our Commitments to You
                  </h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {importantPolicies.map((policy, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center text-purple-600">
                          {policy.icon}
                        </div>
                        <h4 className="font-semibold text-gray-800 text-sm">{policy.title}</h4>
                      </div>
                      <p className="text-xs text-gray-600">{policy.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Limitation of Liability */}
              <div className="p-6 md:p-8 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center text-purple-600">
                    <FaUserShield className="w-4 h-4" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">
                    Limitation of Liability
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  To the maximum extent permitted by law, SKK Jewelry shall not be liable for any indirect, 
                  incidental, special, consequential, or punitive damages, including without limitation, 
                  loss of profits, data, use, goodwill, or other intangible losses, resulting from:
                </p>
                <ul className="space-y-2 ml-2">
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <FaCheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Your use or inability to use our website or products</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <FaCheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Any conduct or content of any third party on our website</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <FaCheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Unauthorized access, use, or alteration of your transmissions or content</span>
                  </li>
                </ul>
              </div>

              {/* Governing Law */}
              <div className="p-6 md:p-8 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center text-purple-600">
                    <FaGavel className="w-4 h-4" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">
                    Governing Law
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  These Terms shall be governed and construed in accordance with the laws of India, 
                  without regard to its conflict of law provisions. Any dispute arising from these terms 
                  or your use of our website shall be subject to the exclusive jurisdiction of the courts 
                  in Mumbai, India.
                </p>
              </div>

              {/* Contact Section */}
              <div id="contact" className="p-6 md:p-8 bg-gradient-to-r from-purple-50 to-pink-50 scroll-mt-20">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                    <FaShoppingCart className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">
                    Need Help?
                  </h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  If you have any questions about these Terms & Conditions, please contact our support team:
                </p>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="mailto:info@skkjewellers.com"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all text-sm"
                  >
                    <span>📧</span>
                    info@skkjewellers.com
                  </a>
                  <a 
                    href="tel:+9118001234567"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-all text-sm"
                  >
                    <span>📞</span>
                    +91 92856 68748
                  </a>
                </div>
              </div>
            </div>

            {/* Footer Note */}
            <div className="mt-6 text-center text-xs text-gray-500">
              <p>By continuing to use our website, you agree to these Terms & Conditions.</p>
              <p className="mt-1">© {new Date().getFullYear()} SKK Jewelry. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;